import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { AccessTime, Coffee, PlayArrow, Pause } from '@mui/icons-material';
import { attendanceAPI } from '../services/api';

const WorkingTimeTracker = () => {
  const [attendance, setAttendance] = useState(null);
  const [workingSeconds, setWorkingSeconds] = useState(0);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [isOnBreak, setIsOnBreak] = useState(false);
  
  const loginTimeRef = useRef(null);
  const breakStartTimeRef = useRef(null);
  const totalBreakSecondsRef = useRef(0);
  const animationFrameRef = useRef(null);
  const workingAtBreakStartRef = useRef(null);
  const isOnBreakRef = useRef(false);
  const accumulatedWorkRef = useRef(null);

  useEffect(() => {
    loadAttendance();
    
    const updateTimeLoop = () => {
      updateTimes();
      animationFrameRef.current = requestAnimationFrame(updateTimeLoop);
    };
    
    animationFrameRef.current = requestAnimationFrame(updateTimeLoop);
    
    const attendanceCheckInterval = setInterval(() => {
      loadAttendance();
    }, 60000); // Check every 60 seconds
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearInterval(attendanceCheckInterval);
    }; // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  const loadAttendance = async () => {
    try {
      const response = await attendanceAPI.getToday();
      if (response.data.success && response.data.data) {
        const newAttendance = response.data.data;
        const newIsOnBreak = !!newAttendance.attendance?.active_break;
        
        if (newAttendance.attendance?.login_time && !loginTimeRef.current) {
          loginTimeRef.current = new Date(newAttendance.attendance.login_time);
          
          const now = new Date();
          const loginTime = loginTimeRef.current;
          const totalElapsed = Math.floor((now - loginTime) / 1000);
          
          const initialBreakSeconds = (newAttendance.attendance.total_break_time || 0) * 60;
          totalBreakSecondsRef.current = initialBreakSeconds;
          setBreakSeconds(initialBreakSeconds);
          
          const serverWorkingSeconds = (newAttendance.attendance.net_working_time || 0) * 60;
          
          if (newIsOnBreak && newAttendance.attendance.active_break) {
            const breakStartTime = new Date(newAttendance.attendance.active_break.start_time);
            const breakStartElapsed = Math.floor((now - breakStartTime) / 1000);
            
            let workBeforeBreak;
            if (serverWorkingSeconds > 0) {
              workBeforeBreak = serverWorkingSeconds;
            } else {
              workBeforeBreak = Math.max(0, totalElapsed - initialBreakSeconds - breakStartElapsed);
            }
            
            workingAtBreakStartRef.current = workBeforeBreak;
            accumulatedWorkRef.current = workBeforeBreak;
            setWorkingSeconds(workBeforeBreak);
            breakStartTimeRef.current = breakStartTime;
            totalBreakSecondsRef.current = initialBreakSeconds;
            setBreakSeconds(initialBreakSeconds);
            setIsOnBreak(true);
            isOnBreakRef.current = true;
          } else {
            let workSeconds;
            if (serverWorkingSeconds > 0) {
              workSeconds = serverWorkingSeconds;
            } else {
              workSeconds = totalElapsed - initialBreakSeconds;
            }
            
            accumulatedWorkRef.current = workSeconds;
            setWorkingSeconds(workSeconds);
          }
        } else if (newAttendance.attendance?.login_time) {
          if (newIsOnBreak && !isOnBreakRef.current) {
            const now = new Date();
            const breakStartTime = new Date(newAttendance.attendance.active_break.start_time);
            const breakStartElapsed = Math.floor((now - breakStartTime) / 1000);
            workingAtBreakStartRef.current = accumulatedWorkRef.current;
            breakStartTimeRef.current = breakStartTime;
            setIsOnBreak(true);
            isOnBreakRef.current = true;
          } else if (!newIsOnBreak && isOnBreakRef.current) {
            if (breakStartTimeRef.current) {
              const now = new Date();
              const currentBreakSeconds = Math.floor((now - breakStartTimeRef.current) / 1000);
              totalBreakSecondsRef.current += currentBreakSeconds;
              setBreakSeconds(totalBreakSecondsRef.current);
            }
            breakStartTimeRef.current = null;
            setIsOnBreak(false);
            isOnBreakRef.current = false;
          }
        }
        
        setAttendance(newAttendance);
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const updateTimes = () => {
    if (!loginTimeRef.current) return;
    
    const now = new Date();
    const totalElapsed = Math.floor((now - loginTimeRef.current) / 1000);
    
    let currentBreakSeconds = 0;
    if (isOnBreakRef.current && breakStartTimeRef.current) {
      currentBreakSeconds = Math.floor((now - breakStartTimeRef.current) / 1000);
    }
    
    const totalBreak = totalBreakSecondsRef.current + currentBreakSeconds;
    setBreakSeconds(totalBreak);
    
    if (!isOnBreakRef.current) {
      const workSeconds = totalElapsed - totalBreak;
      accumulatedWorkRef.current = Math.max(0, workSeconds);
      setWorkingSeconds(Math.max(0, workSeconds));
    }
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Show not logged in message when not logged in
  if (!attendance?.is_logged_in) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: 'rgb(10, 25, 41)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 1.5,
          px: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(212,175,55,0.2)',
          border: '1px solid #D4AF37',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            background: 'rgba(198, 40, 40, 0.15)',
            border: '1px solid rgba(198, 40, 40, 0.4)',
          }}
        >
          <Pause sx={{ fontSize: 16, color: '#EF5350' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: '#EF5350',
              letterSpacing: 1,
              fontSize: '0.7rem',
            }}
          >
            NOT LOGGED IN
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: 'rgb(10, 25, 41)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        p: 1.5,
        px: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(212,175,55,0.2)',
        border: '1px solid #D4AF37',
      }}
    >
      {/* Working Time */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          background: isOnBreak 
            ? 'rgba(212, 175, 55, 0.15)' 
            : 'rgba(46, 125, 50, 0.2)',
          border: `1px solid ${isOnBreak ? '#D4AF37' : '#4CAF50'}`,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isOnBreak ? '#D4AF37' : '#4CAF50',
            boxShadow: isOnBreak 
              ? '0 0 8px rgba(212, 175, 55, 0.8)' 
              : '0 0 8px rgba(76, 175, 80, 0.8)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: isOnBreak ? '#D4AF37' : '#81C784',
              letterSpacing: 1,
              fontSize: '0.6rem',
              lineHeight: 1,
            }}
          >
            {isOnBreak ? 'PAUSED' : 'WORKING'}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", "Roboto Mono", monospace',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#FFFFFF',
              letterSpacing: 1,
              lineHeight: 1.2,
            }}
          >
            {formatTime(workingSeconds)}
          </Typography>
        </Box>
      </Box>

      {/* Break Time */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          background: 'rgba(34, 77, 9, 0.15)',
          border: '1px solid #D4AF37',
        }}
      >
        <Coffee sx={{ fontSize: 14, color: '#D4AF37' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#D4AF37',
              letterSpacing: 1,
              fontSize: '0.6rem',
              lineHeight: 1,
            }}
          >
            BREAK
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", "Roboto Mono", monospace',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#FFFFFF',
              letterSpacing: 1,
              lineHeight: 1.2,
            }}
          >
            {formatTime(breakSeconds)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkingTimeTracker;